import {
  useRulesDetailModalContext,
  RulesDetailModalProvider,
} from 'contexts/RulesDetailModalContext';
import download from 'downloadjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  useGetAllRules,
  invalidateAllRulesQuery,
  useRulesJsonDownload,
  type Rule,
} from '@frontend/api';
import {
  Button,
  ButtonGroup,
  Tooltip,
  useToast,
} from '@frontend/legacy-components';

import { SUPPLIER_ROLES, PERMISSIONS } from 'constants/all';

import ConfigItemListPage from 'modules/configurePages/configItemListPage/ConfigItemListPage';
import ConfigItemListPageList from 'modules/configurePages/configItemListPage/ConfigItemListPageList';

import { getGroupMemberSupplierRoles } from 'store/session/selectors';

import { useSearchParams } from 'utils/hooks/useSearchParams';

import { ReqsReviewConfigProvider } from './ReqsReviewConfigContext';
import { RulesEmptyState } from './components/RulesEmptyState';
import { RulesImportModal } from './components/RulesImportModal';

const RulesListPageContent = () => {
  const toast = useToast();
  const { openModal, closeModal } = useRulesDetailModalContext();

  const [searchParams] = useSearchParams<{ rule_id?: string }>();
  const ruleIdFromUrl = searchParams.rule_id;

  const isReadOnlyPermission =
    useSelector(getGroupMemberSupplierRoles)[SUPPLIER_ROLES.PROCESSES] ===
    PERMISSIONS.READ_ONLY;
  const { data: rules, isLoading: isLoadingRules } = useGetAllRules();
  const { refetch: downloadRules } = useRulesJsonDownload({
    queryOptions: {
      enabled: false,
    },
  });

  const items = useMemo(
    () =>
      rules?.map((rule) => ({
        uid: rule.uuid,
        name: rule.name,
        description: rule.description ?? '',
        to: `/processes/rules?rule_id=${rule.uuid}`,
      })) ?? [],
    [rules],
  );

  const selectedRule = useMemo(
    () => rules?.find((r) => r.uuid === ruleIdFromUrl),
    [rules, ruleIdFromUrl],
  );

  const onRuleCreateSuccess = useCallback(
    (data: Rule) => {
      toast({
        title: 'Created Rule',
        description: `Rule "${data.name}" created successfully`,
        status: 'success',
      });
      invalidateAllRulesQuery();
    },
    [toast],
  );

  const onRuleUpdateSuccess = useCallback(
    (data: Rule) => {
      toast({
        title: 'Updated Rule',
        description: `Rule "${data.name}" updated successfully`,
        status: 'success',
      });
      invalidateAllRulesQuery();
    },
    [toast],
  );

  const onRuleDeleteSuccess = useCallback(
    (data: Rule) => {
      toast({
        title: 'Deleted Rule',
        description: `Rule "${data.name}" deleted successfully`,
        status: 'success',
      });
      invalidateAllRulesQuery();
    },
    [toast],
  );

  const handleCloseModal = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleOpenModal = useCallback(() => {
    openModal({
      isReadOnly: isReadOnlyPermission,
      onSuccess: onRuleCreateSuccess,
      onDelete: onRuleDeleteSuccess,
      mode: 'create',
    });
  }, [
    openModal,
    isReadOnlyPermission,
    onRuleCreateSuccess,
    onRuleDeleteSuccess,
  ]);

  // Open modal when rule ID is in URL
  useEffect(() => {
    if (!isLoadingRules && ruleIdFromUrl && selectedRule) {
      openModal({
        rule: selectedRule,
        isReadOnly: isReadOnlyPermission,
        onSuccess: onRuleUpdateSuccess,
        onDelete: onRuleDeleteSuccess,
        mode: 'update',
      });
    }
  }, [
    isLoadingRules,
    ruleIdFromUrl,
    selectedRule,
    openModal,
    isReadOnlyPermission,
    onRuleUpdateSuccess,
    onRuleDeleteSuccess,
  ]);

  // Check for invalid rule ID
  useEffect(() => {
    if (!isLoadingRules && ruleIdFromUrl && !selectedRule) {
      toast({
        title: 'Failed to open rule',
        description: "This rule couldn't be found",
        status: 'error',
      });
      handleCloseModal();
    }
  }, [isLoadingRules, ruleIdFromUrl, selectedRule, toast, handleCloseModal]);

  const handleDownload = async () => {
    const result = await downloadRules();
    if (result.data) {
      download(
        JSON.stringify(result.data, null, 2),
        'rules.json',
        'application/json',
      );
    }
  };

  const [isImportOpen, setIsImportOpen] = useState(false);

  const handleOpenImport = useCallback(() => setIsImportOpen(true), []);
  const handleCloseImport = useCallback(() => setIsImportOpen(false), []);

  return (
    <ReqsReviewConfigProvider>
      <ConfigItemListPage pageTitle="Rules">
        <ConfigItemListPageList
          items={items}
          isSearchable
          searchPlaceholder="Search for Rules"
          emptyState={({ isFiltered }) => (
            <RulesEmptyState
              isFiltered={isFiltered}
              isLoadingRules={isLoadingRules}
              isReadOnlyPermission={isReadOnlyPermission}
              onCreateRule={handleOpenModal}
            />
          )}
        />
        <ButtonGroup>
          {isReadOnlyPermission ? (
            <Tooltip label="You don't have permission to create new rules">
              <Button isDisabled>+ New Rule</Button>
            </Tooltip>
          ) : (
            <Button onClick={handleOpenModal} isDisabled={isReadOnlyPermission}>
              + New Rule
            </Button>
          )}
          {isReadOnlyPermission ? (
            <Tooltip label="You don't have permission to import rules">
              <Button isDisabled>Import rules</Button>
            </Tooltip>
          ) : (
            <Button
              onClick={handleOpenImport}
              isDisabled={isReadOnlyPermission}
            >
              + Import rules
            </Button>
          )}
          <Button variant="secondary" onClick={handleDownload}>
            Download Rules
          </Button>
        </ButtonGroup>
        <RulesImportModal isOpen={isImportOpen} onClose={handleCloseImport} />
      </ConfigItemListPage>
    </ReqsReviewConfigProvider>
  );
};

export const RulesListPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [searchParams] = useSearchParams<{ rule_id?: string }>();
  const ruleIdFromUrl = searchParams.rule_id;

  const handleCloseModal = useCallback(() => {
    if (ruleIdFromUrl) {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('rule_id');
      history.replace({ search: newSearchParams.toString() });
    }
  }, [history, ruleIdFromUrl, location.search]);

  return (
    <RulesDetailModalProvider onClose={handleCloseModal}>
      <RulesListPageContent />
    </RulesDetailModalProvider>
  );
};
